/**
 * ChatPage — main module entry point.
 *
 * Wired to Zustand store + React Query + transport hooks.
 * Preserves all behavioral contracts:
 * - Canvas auto-open with version tracking
 * - Frontend-tool catalog registration when the query resolves
 * - Hero mode detection
 * - Session create-on-first-message flow
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { AppState } from "@strands/agent-transport";
import { InputBar } from "./InputBar";
import { WebdavBrowser } from "./WebdavBrowser";
import { FolderConflictDialog } from "./FolderConflictDialog";
import { HeroBlock } from "./HeroBlock";
import { RecentTasks } from "./RecentTasks";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { SideStack } from "../layout/SideStack";
import { useLayoutStore } from "../layout/LayoutProvider";
import { MainSlot } from "../layout/MainSlot";
import { OverlayRoot } from "../layout/OverlayRoot";
import { ChatActionsProvider } from "./ChatActionsContext";
import { SessionOwnerBanner } from "./SessionOwnerBanner";
import { ApprovalBanner } from "./ApprovalBanner";
import { ChatHeader } from "./ChatHeader";
import { sessionMetaQueryOptions } from "../queries/session-queries";
import { frontendToolsQueryOptions } from "../queries/frontend-tools-queries";
import { featuresQueryOptions } from "../queries/features-queries";
import { importWebdav } from "../api/webdav";
import { useQuery } from "@tanstack/react-query";
import { useChatActions, clearSkipLoad } from "../hooks/use-chat-actions";
import { useSessionRefresh } from "../hooks/use-session-refresh";
import { useSessions } from "../hooks/use-sessions";
import { useMessages, useStreaming, useAppState, useChatStoreApi } from "../stores";
import { MessageList } from "./MessageList";
import { useDocSync, uploadFolderToSession } from "../hooks/use-doc-sync";
import { uploadAttachmentsToSession } from "../lib/attachments";
import { useFilesWatch } from "../hooks/use-files-watch";
import { currentUserId, loadHandle, removeHandle, saveHandle } from "../lib/handleStore";
import { useFolderSyncStore } from "../stores/folder-sync-store";
import { useSpaceBootstrap } from "@/modules/spaces/use-space-bootstrap";

interface Props {
  user: { id: string; email: string; name: string; role: "admin" | "user" } | null;
}

/**
 * Chat content for the `/` route. The outer chat-layout wrapper, sidebar,
 * and ControlPanel takeover live in `_app.tsx` (the pathless layout) — all
 * we render here is what goes inside `<Outlet />`: the chat column, the
 * optional MainSlot (canvas/docx), and overlay/dialog roots.
 */
export function ChatPage({ user }: Props) {
  const navigate = useNavigate();
  // Resolve / persist the active space before any space-scoped query fires.
  useSpaceBootstrap(!!user);
  const { mainView, openMain, closeMain } = useLayoutStore();
  const pendingAgentRef = useAgentDraftStore((s) => s.pendingRef);
  const setPendingAgentRef = useAgentDraftStore((s) => s.setPendingRef);
  const pendingConnectorIds = useAgentDraftStore((s) => s.pendingConnectorIds);
  const setPendingConnectorIds = useAgentDraftStore((s) => s.setPendingConnectorIds);
  const setStarting = useAgentDraftStore((s) => s.setStarting);
  const pendingFolderHandle = useAgentDraftStore((s) => s.pendingFolderHandle);
  const setPendingFolderHandle = useAgentDraftStore((s) => s.setPendingFolderHandle);
  const pendingWebdavItems = useAgentDraftStore((s) => s.pendingWebdavItems);
  const setPendingWebdavItems = useAgentDraftStore((s) => s.setPendingWebdavItems);
  const pendingFiles = useAgentDraftStore((s) => s.pendingFiles);
  const setPendingFiles = useAgentDraftStore((s) => s.setPendingFiles);
  const setHeroSync = useAgentDraftStore((s) => s.setHeroSync);
  const { data: features } = useQuery(featuresQueryOptions());
  const networkFolderEnabled = !!features?.networkFolder;
  const [webdavOpen, setWebdavOpen] = useState(false);

  const {
    sessions,
    activeId,
    setActiveId,
    createSession,
    selectSession,
    deleteSession,
    updateSessionTitle,
    invalidateSessions,
  } = useSessions();

  // Canvas close-on-switch reconciliation
  const handleSessionLoaded = useCallback(
    (loaded: AppState) => {
      const c = loaded.canvas as { content?: string } | undefined;
      if (!c?.content && mainView?.id === "canvas") {
        closeMain();
      }
    },
    [mainView, closeMain],
  );

  const {
    sendMessage,
    sendToolResult,
    respondToInterrupt,
    stopStreaming,
    updateState,
    registerTool,
    loadHistory,
    onTitleUpdateRef,
  } = useChatActions({
    onTitleUpdate: updateSessionTitle,
    onSessionLoaded: handleSessionLoaded,
  });

  // Reload history when a background turn (webhook/cron) for the currently-
  // open session halts/finishes — without this the new events wouldn't
  // appear until F5.
  useSessionRefresh({ reload: loadHistory });

  // Wire onTitleUpdate ref
  onTitleUpdateRef.current = updateSessionTitle;

  // Zustand selectors
  const messages = useMessages();
  const isStreaming = useStreaming();
  const state = useAppState();

  // Load history when activeId changes
  const storeApi = useChatStoreApi();
  const prevActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    const store = storeApi.getState();
    const storeSid = store.activeSessionId;
    // Stale messages remain in the store after navigating /→/agents→/ with
    // setActiveId(null); without this check the hero would render with the
    // previous session's messages still in `messages`.
    const stale = activeId === null && store.messages.length > 0;
    if (activeId === prevActiveIdRef.current && storeSid === activeId && !stale) return;
    // Genuine session switch / hero → release the create-send skip so
    // returning to that session later reloads its history. No-op while the
    // freshly-sent session is still the active one (preserves the skip
    // across the /new→/?sid= remount).
    clearSkipLoad(activeId);
    prevActiveIdRef.current = activeId;
    if (activeId) setPendingAgentRef(null);
    if (activeId) {
      loadHistory(activeId);
    } else {
      storeApi.setState((s) => {
        s.messages = [];
        s.partsMap = {};
        s.streaming = false;
        s.appState = {};
        s.activeSessionId = null;
      });
      // Canvas/docx живут на сессию. В hero-режиме сессии нет, а
      // mainView висит в отдельном Context и сам не сбрасывается —
      // без этого закрытия leftover canvas разворачивается фуллскрином
      // (mainActive && !heroMode-блокировки splitMode → expandedMode=true).
      closeMain();
    }
  }, [activeId, loadHistory, setPendingAgentRef, storeApi, closeMain]);

  // mainView is session-scoped (canvas/infographic both reflect the active
  // session's state), but it lives in a Context above the route, so it
  // persists across navigation and session switches. Close it on any
  // active-session transition — including the fresh-mount-with-stale-view
  // case (prev === undefined). The auto-open effects below will reopen the
  // view for the new session if its state still warrants it.
  const prevActiveForMainRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevActiveForMainRef.current;
    prevActiveForMainRef.current = activeId;
    const transitioning = prev === undefined || prev !== activeId;
    if (transitioning && mainView !== null) closeMain();
  }, [activeId, mainView, closeMain]);

  // Canvas auto-open with version tracking
  const lastSeenCanvasVersionRef = useRef<number | null>(null);
  const prevCanvasActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevCanvasActiveIdRef.current !== activeId) {
      prevCanvasActiveIdRef.current = activeId;
      lastSeenCanvasVersionRef.current = null;
      // На смене сессии state.canvas ещё несёт данные прошлой сессии:
      // loadHistory только запущен, а в hero-режиме reset-эффект ещё
      // не отработал. Открывать сейчас — значит развернуть фуллскрином
      // пустой/чужой canvas. Дождёмся следующего рендера, где state.canvas
      // уже свежий — этот же effect запустится повторно по зависимости.
      return;
    }
    const c = state.canvas as { content?: string; updated_at?: number } | undefined;
    const currentVersion = c?.updated_at ?? null;
    if (mainView?.id === "canvas") {
      lastSeenCanvasVersionRef.current = currentVersion;
      return;
    }
    if (!c?.content) return;
    if (currentVersion !== null && currentVersion === lastSeenCanvasVersionRef.current) return;
    openMain("canvas");
  }, [state.canvas, mainView, openMain, activeId]);

  // Infographic auto-open — same version-tracking idiom as canvas above.
  // Triggers on each new render_infographic call (StateBroadcaster.replace
  // bumps updated_at), but stays quiet on session-load and when the user
  // has manually opened another main view. Re-issuing openMain WITHOUT
  // props clears any pinned-historical state so the viewer returns to
  // showing the latest from state.infographic.
  const lastSeenInfographicVersionRef = useRef<number | null>(null);
  const prevInfographicActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevInfographicActiveIdRef.current !== activeId) {
      prevInfographicActiveIdRef.current = activeId;
      lastSeenInfographicVersionRef.current = null;
      return;
    }
    const v = state.infographic as { blocks?: unknown; updated_at?: number } | undefined;
    const currentVersion = v?.updated_at ?? null;
    if (mainView?.id === "infographic") {
      lastSeenInfographicVersionRef.current = currentVersion;
      return;
    }
    if (!Array.isArray(v?.blocks) || v.blocks.length === 0) return;
    if (currentVersion !== null && currentVersion === lastSeenInfographicVersionRef.current) return;
    openMain("infographic");
  }, [state.infographic, mainView, openMain, activeId]);

  // Email draft auto-open — same version-tracking idiom as canvas /
  // infographic. `compose_email` is now a state-canvas tool: each call
  // bumps `state.email_draft.updated_at`, and the user keeps the form
  // open across turns. Re-opening on bump is intentional so the user
  // sees the agent's revisions land in the form.
  const lastSeenEmailDraftVersionRef = useRef<number | null>(null);
  const prevEmailDraftActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevEmailDraftActiveIdRef.current !== activeId) {
      prevEmailDraftActiveIdRef.current = activeId;
      lastSeenEmailDraftVersionRef.current = null;
      return;
    }
    const d = state.email_draft as { subject?: string; body?: string; to?: string; updated_at?: number } | undefined;
    const currentVersion = d?.updated_at ?? null;
    if (mainView?.id === "mail-compose") {
      lastSeenEmailDraftVersionRef.current = currentVersion;
      return;
    }
    // Treat the draft as «present» if any of the core fields has content.
    if (!d?.subject && !d?.body && !d?.to) return;
    if (currentVersion !== null && currentVersion === lastSeenEmailDraftVersionRef.current) return;
    openMain("mail-compose");
  }, [state.email_draft, mainView, openMain, activeId]);

  // Invalidate session list after streaming ends (picks up title changes etc.)
  useEffect(() => {
    if (!isStreaming) invalidateSessions();
  }, [isStreaming, invalidateSessions]);

  // Register frontend tools when the catalog resolves. The list is fetched
  // from /api/frontend-tools so the toolspec the LLM sees is identical to
  // what headless callers (webhook fires, cron routines) register.
  const { data: frontendTools } = useQuery(frontendToolsQueryOptions());
  useEffect(() => {
    if (!frontendTools) return;
    const unregister = frontendTools.map((tool) => registerTool(tool));
    return () => unregister.forEach((u) => u());
  }, [frontendTools, registerTool]);

  // Send flow: create session on first message if in hero mode
  const handleSend = useCallback(
    async (text: string, command?: { label: string }) => {
      let sid = activeId;
      const heroHandle = !activeId ? pendingFolderHandle : null;
      const heroWebdav = !activeId ? pendingWebdavItems : null;
      const heroFiles = !activeId ? pendingFiles : null;
      if (!sid) {
        // Mark the hero→chat transition so the unmount cleanup below
        // doesn't drop pendingFolderHandle between /new unmounting and
        // /?sid=X mounting (the bridge effect needs it to still be set
        // when the new ChatPage runs its folder-attach effect).
        setStarting(true);
        sid = await createSession(
          pendingAgentRef ?? undefined,
          pendingConnectorIds,
        );
        // The override has been baked into the new session — drop the draft
        // so the next hero render starts fresh from the agent's defaults.
        if (pendingConnectorIds !== null) setPendingConnectorIds(null);
        // sendMessage writes activeSessionId itself on the skipLoadRef path;
        // calling setActiveId here first would make currentSid === overrideSid
        // inside sendMessage, skipLoadRef wouldn't get set, and the mount-time
        // loadHistory would wipe the messages/streaming state we just pushed.
      }
      try {
        // Gate /api/runs on folder upload — without this the agent's first
        // turn races the bridge-effect's finishConnect and sees an empty
        // workspace. pendingFolderHandle stays set on purpose so the new
        // ChatPage's bridge effect still attaches the handle to useDocSync;
        // its finishConnect("merge") finds the manifest already matching the
        // server, skips the upload, and just starts watchers.
        if (heroHandle) {
          // Drive the optimistic user bubble + sync step from one record.
          // Starts `uploading`; ticks current/total via getState (the
          // closure's heroSync would be stale per progress event); flips
          // to `done` in finally so the step settles into the turn rail
          // as a completed step instead of vanishing.
          setHeroSync({
            sessionId: sid,
            text,
            folderName: heroHandle.name,
            current: 0,
            total: 0,
            status: "uploading",
          });
          try {
            await uploadFolderToSession(sid, heroHandle, (p) => {
              const cur = useAgentDraftStore.getState().heroSync;
              if (cur) setHeroSync({ ...cur, current: p.current, total: p.total });
            });
          } finally {
            const cur = useAgentDraftStore.getState().heroSync;
            if (cur) setHeroSync({ ...cur, status: "done" });
          }
        } else if (heroWebdav && heroWebdav.length > 0) {
          // Network-folder pull: same gate + sync-step UX as the local case,
          // but the backend does the copy in one call (no per-file progress,
          // so the step shows the indeterminate "Синхронизирую файлы…" bar).
          setHeroSync({
            sessionId: sid,
            text,
            folderName: "Сетевая папка",
            current: 0,
            total: 0,
            status: "uploading",
          });
          try {
            await importWebdav(sid, heroWebdav);
          } finally {
            setPendingWebdavItems(null);
            const cur = useAgentDraftStore.getState().heroSync;
            if (cur) setHeroSync({ ...cur, status: "done" });
          }
        }
        // Attachments are independent of a folder/webdav source — upload them
        // too, into attachments/. Drive the sync-step UI only when they're the
        // sole source so we don't clobber a folder's progress record.
        if (heroFiles && heroFiles.length > 0) {
          const soleSource = !heroHandle && !(heroWebdav && heroWebdav.length > 0);
          if (soleSource) {
            setHeroSync({
              sessionId: sid,
              text,
              folderName: null,
              current: 0,
              total: heroFiles.length,
              status: "uploading",
              kind: "files",
            });
          }
          try {
            await uploadAttachmentsToSession(sid, heroFiles, (current, total) => {
              if (!soleSource) return;
              const cur = useAgentDraftStore.getState().heroSync;
              if (cur) setHeroSync({ ...cur, current, total });
            });
          } finally {
            setPendingFiles(null);
            if (soleSource) {
              const cur = useAgentDraftStore.getState().heroSync;
              if (cur) setHeroSync({ ...cur, status: "done" });
            }
          }
        }
        await sendMessage(text, sid, command);
      } finally {
        setStarting(false);
      }
    },
    [activeId, createSession, sendMessage, pendingAgentRef, pendingConnectorIds, setPendingConnectorIds, setStarting, pendingFolderHandle, pendingWebdavItems, setPendingWebdavItems, pendingFiles, setPendingFiles, setHeroSync],
  );

  // Auto-send a draft handed over from another page (the space dashboard's
  // hero input). The store entry is consumed exactly once and cleared so
  // a refresh inside the chat doesn't re-fire the same prompt.
  const pendingDraftText = useAgentDraftStore((s) => s.pendingText);
  const setPendingDraftText = useAgentDraftStore((s) => s.setPendingText);
  const pendingDraftCommand = useAgentDraftStore((s) => s.pendingCommand);
  const setPendingDraftCommand = useAgentDraftStore((s) => s.setPendingCommand);
  const starting = useAgentDraftStore((s) => s.starting);
  useEffect(() => {
    if (!pendingDraftText) return;
    if (activeId) return; // only fire while still in hero mode
    const text = pendingDraftText;
    const command = pendingDraftCommand ?? undefined;
    setPendingDraftText(null);
    setPendingDraftCommand(null);
    handleSend(text, command).finally(() => setStarting(false));
  }, [pendingDraftText, pendingDraftCommand, activeId, handleSend, setPendingDraftText, setPendingDraftCommand, setStarting]);

  // Per-session ownership info — drives the read-only banner and gates
  // write-y side-effects (folder sync, etc.). Hero mode never has an
  // active session, so the gate only matters when activeId is set.
  const { data: sessionMeta } = useQuery(sessionMetaQueryOptions(activeId));
  const readOnly = !!activeId && sessionMeta?.is_owner === false;

  // ── Folder sync ──
  // Folder sync triggers writes (diff/upload). For non-owners we pass
  // `null` so the hook stays inert — they can still browse files via
  // the read-only file panel that hits the GET endpoints directly.
  const docs = useDocSync(readOnly ? null : activeId, isStreaming);
  useFilesWatch(activeId);
  const folderPickerSupported = typeof window !== "undefined" && "showDirectoryPicker" in window;

  // Mirror docs state into a shared store so side panels (FilesPanel) can
  // render the tree + connect button without prop drilling through layout.
  // disconnect is wrapped so the saved IndexedDB handle is also dropped.
  // In hero (no activeId) the folder isn't connected yet — only stashed in
  // pendingFolderHandle — so the disconnect chip drops the stash instead.
  const wrappedDisconnect = useCallback(() => {
    if (!activeId) {
      setPendingFolderHandle(null);
      setPendingWebdavItems(null);
      return;
    }
    docs.disconnect();
    const uid = currentUserId();
    if (uid) removeHandle(uid, activeId).catch(() => {});
    useFolderSyncStore.getState().set({ pendingHandleName: null, staleHandleName: null, regrantPermission: async () => {} });
  }, [docs, activeId, setPendingFolderHandle, setPendingWebdavItems]);

  // Same race guard as the persist effect: in the render where activeId just
  // changed but the disconnect hasn't applied yet, treat this session as
  // having no folder so the side panel doesn't briefly flash the old name.
  const handleBelongsHere = docs.connectedSessionId === activeId && !!docs.dirHandle;
  useEffect(() => {
    useFolderSyncStore.getState().set({
      docConnected: handleBelongsHere && docs.docConnected,
      connectedSessionId: handleBelongsHere ? activeId : null,
      dirHandleName: handleBelongsHere ? docs.dirHandle?.name ?? null : null,
      docFiles: handleBelongsHere ? docs.docFiles : [],
      uploadedPaths: handleBelongsHere ? docs.uploadedPaths : new Set(),
      pickerSupported: folderPickerSupported,
      pickFolder: docs.pickFolder,
      disconnect: wrappedDisconnect,
      finishConnect: docs.finishConnect,
    });
  }, [
    handleBelongsHere,
    activeId,
    docs.docConnected,
    docs.dirHandle,
    docs.docFiles,
    docs.uploadedPaths,
    docs.pickFolder,
    docs.finishConnect,
    folderPickerSupported,
    wrappedDisconnect,
  ]);

  // Clear the mirrored connection when ChatPage unmounts (navigating to a
  // section route / space dashboard) so a stale connectedSessionId can't
  // linger in the store after the live hook is gone.
  useEffect(() => {
    return () => {
      useFolderSyncStore.getState().set({ docConnected: false, connectedSessionId: null });
    };
  }, []);

  // Persist handle per session after connect; clear pending state.
  // Guard against the cross-session leak: on a session switch render, docs
  // still carries the previous session's dirHandle until the disconnect
  // setState commits one render later. Saving in that window writes the old
  // handle under the new session's IndexedDB key. Only persist when the
  // handle was actually connected for *this* session.
  useEffect(() => {
    if (!activeId || !docs.dirHandle) return;
    if (docs.connectedSessionId !== activeId) return;
    const uid = currentUserId();
    if (uid) saveHandle(uid, activeId, docs.dirHandle).catch(() => {});
    useFolderSyncStore.getState().set({ pendingHandleName: null, staleHandleName: null, regrantPermission: async () => {} });
  }, [activeId, docs.dirHandle, docs.connectedSessionId]);

  // Auto-reconnect the previously-connected folder for this session, surviving
  // page reloads and browser restarts. Strategy:
  //   1. queryPermission — if granted, just reconnect.
  //   2. else try requestPermission silently (Chrome allows this for persisted
  //      handles, even outside a user gesture).
  //   3. if it throws / is denied, expose a "regrant" callback to the UI so the
  //      user can re-grant on click (proper user gesture).
  // `docs` returns a new object identity each render; pin finishConnect via
  // a ref to keep this effect tied to activeId only.
  const finishConnectRef = useRef(docs.finishConnect);
  finishConnectRef.current = docs.finishConnect;
  const restoredForSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeId || !folderPickerSupported) return;
    if (restoredForSessionRef.current === activeId) return;
    restoredForSessionRef.current = activeId;
    let cancelled = false;
    (async () => {
      // Reset any stale pending state from the previous session.
      useFolderSyncStore.getState().set({ pendingHandleName: null, staleHandleName: null, regrantPermission: async () => {} });
      const uid = currentUserId();
      if (!uid) return;
      let handle: FileSystemDirectoryHandle | null = null;
      try {
        handle = await loadHandle(uid, activeId);
      } catch {
        return;
      }
      if (!handle || cancelled) return;
      const h = handle as unknown as {
        queryPermission: (o: { mode: string }) => Promise<PermissionState>;
        requestPermission: (o: { mode: string }) => Promise<PermissionState>;
      };
      try {
        const perm = await h.queryPermission({ mode: "readwrite" });
        if (cancelled) return;
        if (perm === "granted") {
          await finishConnectRef.current(handle, "merge");
          return;
        }
        // Try silent re-grant. Some Chromium versions accept this for
        // persisted handles; if not, we fall through to the UI prompt.
        try {
          const req = await h.requestPermission({ mode: "readwrite" });
          if (cancelled) return;
          if (req === "granted") {
            await finishConnectRef.current(handle, "merge");
            return;
          }
        } catch {
          /* requires user gesture — handled below */
        }
        if (cancelled) return;
        const stableHandle = handle;
        useFolderSyncStore.getState().set({
          pendingHandleName: stableHandle.name,
          regrantPermission: async () => {
            try {
              const req = await h.requestPermission({ mode: "readwrite" });
              if (req !== "granted") return;
              await finishConnectRef.current(stableHandle, "merge");
              useFolderSyncStore.getState().set({ pendingHandleName: null, regrantPermission: async () => {} });
            } catch {
              /* user dismissed — leave pending in place */
            }
          },
        });
      } catch {
        // Stored handle went stale (folder deleted / permission wiped).
        // Surface the name so the UI can explain why "nothing happened".
        const staleName = handle?.name ?? null;
        removeHandle(uid, activeId).catch(() => {});
        if (!cancelled) {
          useFolderSyncStore.getState().set({ staleHandleName: staleName });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, folderPickerSupported]);

  // Folder picked on the SpaceDashboard composer travels through the draft
  // store. When the user's first message creates a session here, attach the
  // handle to it. Cleared by SpaceDashboard's unmount cleanup if the user
  // never sent — so a stray handle can't get yanked onto an unrelated chat
  // they happen to open afterwards.
  useEffect(() => {
    if (!activeId || !pendingFolderHandle) return;
    finishConnectRef.current(pendingFolderHandle, "merge").catch(() => {});
    setPendingFolderHandle(null);
  }, [activeId, pendingFolderHandle, setPendingFolderHandle]);

  // Mirror SpaceDashboard's guard for the chat hero: if the user picked a
  // folder in hero (handle stashed in pendingFolderHandle) but navigates
  // away without sending, drop the stash so it doesn't auto-attach to
  // whatever session loads next. `starting` is raised by handleSend
  // before the create-session navigation, so the /new→/?sid=X unmount
  // here lets the handle survive long enough for the new ChatPage's
  // bridge effect to consume it.
  useEffect(() => {
    return () => {
      if (!useAgentDraftStore.getState().starting) {
        useAgentDraftStore.getState().setPendingFolderHandle(null);
        useAgentDraftStore.getState().setPendingWebdavItems(null);
        useAgentDraftStore.getState().setPendingFiles(null);
      }
    };
  }, []);

  const chatActions = { registerTool, sendToolResult, respondToInterrupt };

  // `starting` and `pendingDraftText` both gate hero during the transition
  // from SpaceDashboard so the welcome screen doesn't flash before streaming.
  const heroMode = !isStreaming && messages.length === 0 && !pendingDraftText && !starting;

  // Slash menu resolves commands server-side via the active agent — see
  // InputBar.agentRef. For a running session we pass the snapshot's agent
  // ref (whatever the chat started with); InputBar handles hero mode
  // internally from the draft store.
  const agentRefForInput: string | null = activeId
    ? sessions.find((s) => s.id === activeId)?.agent?.ref ?? null
    : null;

  // In hero (no session yet) the docs hook is inert, so the folder chip
  // reflects the stashed pendingFolderHandle instead — same pattern
  // SpaceDashboard uses for its hero composer.
  const heroFolderName = !activeId
    ? pendingFolderHandle?.name ?? (pendingWebdavItems?.length ? "Сетевая папка" : null)
    : null;
  const input = (
    <InputBar
      onSend={handleSend}
      onStop={stopStreaming}
      disabled={false}
      isStreaming={isStreaming}
      sessionId={activeId}
      folderConnected={activeId ? docs.docConnected : (!!pendingFolderHandle || !!pendingWebdavItems?.length)}
      folderName={activeId ? docs.dirHandle?.name ?? null : heroFolderName}
      folderPickerSupported={folderPickerSupported}
      onPickFolder={docs.pickFolder}
      networkFolderEnabled={networkFolderEnabled}
      onPickNetworkFolder={() => setWebdavOpen(true)}
      onDisconnectFolder={wrappedDisconnect}
      heroMode={heroMode}
      syncStatus={docs.syncStatus}
      agentRef={agentRefForInput}
    />
  );

  const mainActive = mainView !== null;
  const splitMode = mainActive && !mainView.expanded && !heroMode;
  const expandedMode = mainActive && !splitMode;

  // Reserve space at the top/bottom of the message list so the absolutely-
  // positioned header and composer never cover the first/last messages.
  // Bottom reserves composer height + 48px so the streamed tail clears the
  // composer with a small, constant gap (the per-frame pin in MessageList
  // keeps it glued there); top falls back to 20px (CSS default) when no
  // header.
  const chatContentRef = useRef<HTMLDivElement>(null);
  const chatHeaderRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const input = chatInputRef.current;
    const header = chatHeaderRef.current;
    const content = chatContentRef.current;
    if (!content) return;
    const update = () => {
      if (input) {
        const reserved = Math.max(input.offsetHeight + 48, 200);
        content.style.setProperty("--chat-input-h", `${reserved}px`);
      }
      if (header) {
        content.style.setProperty(
          "--chat-header-h",
          `${header.offsetHeight + 8}px`,
        );
      } else {
        content.style.removeProperty("--chat-header-h");
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (input) ro.observe(input);
    if (header) ro.observe(header);
    return () => ro.disconnect();
  }, [heroMode, expandedMode, activeId]);

  return (
    <ChatActionsProvider value={chatActions}>
      {!expandedMode && (
        <div className="chat">
          <div className="chat-body">
            <div className="chat-content" ref={chatContentRef}>
              {heroMode ? (
                <HeroBlock
                  extra={
                    <RecentTasks
                      onOpenSession={selectSession}
                      onOpenAll={() => navigate({ to: "/history" })}
                    />
                  }
                >
                  {input}
                </HeroBlock>
              ) : (
                <>
                  {activeId && (
                    <ChatHeader
                      ref={chatHeaderRef}
                      title={
                        sessions.find((s) => s.id === activeId)?.title ?? ""
                      }
                      onBack={() => {
                        // Walk one step up the in-tab history. With fresh
                        // tabs / deep links there's nothing to step back to,
                        // so fall back to hero.
                        if (window.history.length > 1) window.history.back();
                        else setActiveId(null);
                      }}
                      onDelete={() => {
                        // Stop folder sync BEFORE the workspace is wiped.
                        // Otherwise an in-flight server→local reconciliation
                        // sees the emptied server workspace and deletes the
                        // user's original files off disk.
                        docs.disconnect();
                        // Mirror back-navigation: leave wherever the user
                        // came from. Done before the mutation fires so the
                        // URL is no longer /?sid=<deleted> by the time
                        // queryClient invalidates the sessions list.
                        if (window.history.length > 1) window.history.back();
                        else setActiveId(null);
                        deleteSession(activeId);
                      }}
                      canDelete={!readOnly}
                    />
                  )}
                  <MessageList activeId={activeId} />
                  <div className="chat-input-anchor">
                    <div className="chat-input" ref={chatInputRef}>
                      <ApprovalBanner />
                      {readOnly && activeId ? (
                        <SessionOwnerBanner sessionId={activeId} />
                      ) : (
                        input
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {!heroMode && !mainActive && <SideStack state={state} sessionId={activeId} />}
          </div>
        </div>
      )}

      {mainActive && <MainSlot state={state} sessionId={activeId} updateState={updateState} />}
      <OverlayRoot />
      {webdavOpen && (
        <WebdavBrowser
          sessionId={activeId}
          onStash={setPendingWebdavItems}
          onImported={() => { /* files refresh via useFilesWatch */ }}
          onClose={() => setWebdavOpen(false)}
        />
      )}
      <FolderConflictDialog
        serverCount={docs.conflict?.serverCount ?? null}
        onCancel={docs.dismissConflict}
        onMerge={() => {
          const c = docs.conflict;
          if (c) docs.finishConnect(c.handle, "server").catch(() => {});
        }}
        onReplace={() => {
          const c = docs.conflict;
          if (c) docs.finishConnect(c.handle, "local").catch(() => {});
        }}
      />
    </ChatActionsProvider>
  );
}

